import type { ResultsTurn } from '@/state/results-ai/results-ai-thread';
import type { ResultsAiManager } from '@/state/results-ai/useResultsAi';
import { RotateCcw } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import { AiMessage, ChatInputBox, ChatLogFrame, ChatPanelFrame, ChatPanelHeader } from '@/components/ai-chat';
import { FirstUseNotice, SenderRow, TermsAndPrivacyLinks, TextButton, UserMessage } from '@/components/ai-chat';
import { WorkingSteps } from '@/components/ai-chat';
import { formatCredits } from 'helpers/ai-credits';
import { senderName, workingPhase } from '@/state/results-ai/results-ai-thread';
import {
  DRAFT_SUGGESTED_QUESTIONS,
  RESULTS_AI_INPUT_ID,
  RESULTS_AI_OUT_OF_CREDITS_ID,
  SUGGESTED_QUESTIONS,
} from '@/state/results-ai/useResultsAi';
import { WorkspaceSidePanel } from '../../../DashboardLayout/WorkspacePanel';
import { AnswerMarkdown } from './AnswerMarkdown';

/** The field's words, since the panel opens from it on every tab (not "results": decided 2026-10-20). */
export const RESULTS_AI_TITLE = 'Ask about this experiment';

/** For every tab, not only Statistics (decided 2026-10-20): the panel opens from the header on all of them. */
export const GREETING =
  'Ask me anything about this experiment: how far to trust its result, how it is set up, what the brief expected or the debrief concluded, and what to do next. I read the same settings, brief, debrief and figures you see here.';

/** A draft has nothing measured yet: the set-up, the brief and the plan are what it can help with. */
export const DRAFT_GREETING =
  'Ask me anything about this experiment before it starts: whether it is set up the way you mean, whether the hypothesis can be tested, or how long it will need to run. I read its settings and brief.';

/** In the header's chip instead of the view, which a draft has no numbers for. */
export const DRAFT_LABEL = 'Not started yet';

type ResultsAiPanelProps = {
  manager: ResultsAiManager;
  /** What the numbers on screen are for, e.g. "Conversions · All currencies". */
  viewLabel: string;
  /** A draft (decided 2026-10-20 that it gets the chat too): its own greeting and suggestions. */
  draft: boolean;
};

/**
 * The results chat (P-PER-44), docked beside the experiment's page on every tab: the editor AI
 * chat's frame, header, log and input (src/workspaces/components/ai-chat), so it looks exactly like
 * it. Opening moves focus to the input (ResultsInput, which mounts only once the side slot exists).
 */
export const ResultsAiPanel = ({ manager, viewLabel, draft }: ResultsAiPanelProps) => {
  return (
    <WorkspaceSidePanel>
      <ChatPanelFrame
        label={RESULTS_AI_TITLE}
        markerAttribute="data-results-ai-panel"
        edge="rounded-panel border shadow-lg xl:shadow-none"
        header={
          <ChatPanelHeader
            title={RESULTS_AI_TITLE}
            state={manager.phase ?? 'idle'}
            closeLabel="Close Ask about this experiment"
            onClose={() => manager.setOpen(false)}
          >
            <span className="ml-1 inline-flex h-[26px] min-w-0 items-center rounded-md border border-line px-2 text-micro text-foreground">
              <span className="truncate">{draft ? DRAFT_LABEL : viewLabel}</span>
            </span>
          </ChatPanelHeader>
        }
        log={<ResultsLog manager={manager} draft={draft} />}
        footer={<ResultsInput manager={manager} />}
      />
    </WorkspaceSidePanel>
  );
};

type ManagerProps = { manager: ResultsAiManager };

type ResultsLogProps = ManagerProps & { draft: boolean };

const ResultsLog = ({ manager, draft }: ResultsLogProps) => {
  const suggestions = draft ? DRAFT_SUGGESTED_QUESTIONS : SUGGESTED_QUESTIONS;
  const { turns, acknowledged } = manager;
  const endRef = useRef<HTMLDivElement>(null);
  const last = turns.at(-1);
  const scrollKey = `${turns.length}:${last?.phase}:${last?.steps.length}:${last?.answer.length}`;

  // Keeps the newest words in view as the answer streams. Scroll sync, not data fetching.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [scrollKey]);

  return (
    <ChatLogFrame>
      {manager.isLoading && <p className="text-micro text-dim">Loading the conversation…</p>}
      {manager.isError && (
        <AiMessage>
          <p role="alert">The conversation could not be loaded.</p>
          <TextButton onClick={manager.reload}>Try again</TextButton>
        </AiMessage>
      )}
      {acknowledged ? (
        <AiMessage>
          <p>{draft ? DRAFT_GREETING : GREETING}</p>
          {turns.length === 0 && !manager.isLoading && (
            <div className="mt-2 flex flex-col gap-1.5">
              {suggestions.map((question) => (
                <TextButton key={question} onClick={() => manager.ask(question)}>
                  {question}
                </TextButton>
              ))}
            </div>
          )}
        </AiMessage>
      ) : (
        <AiMessage>
          <FirstUseNotice onAcknowledge={() => void manager.acknowledge()}>
            Before you start: the AI assistant is covered by our <TermsAndPrivacyLinks />. What you type, and this
            experiment&apos;s settings, brief, debrief and results, and its website&apos;s learnings when they are on,
            are sent to our AI provider to answer you. The conversation is saved with the experiment for 12 months, and
            everyone who can open the experiment can read it. Each answer uses AI credits.
          </FirstUseNotice>
          {manager.noticeError && (
            <p role="alert" className="mt-2 text-micro text-danger">
              {manager.noticeError}
            </p>
          )}
        </AiMessage>
      )}
      {turns.map((turn) => (
        <TurnView key={turn.key} turn={turn} manager={manager} />
      ))}
      <AnswerAnnouncer turn={last} />
      <div ref={endRef} />
    </ChatLogFrame>
  );
};

type TurnViewProps = { turn: ResultsTurn; manager: ResultsAiManager };

const TurnView = ({ turn, manager }: TurnViewProps) => {
  const phase = workingPhase(turn);
  const footnote = turn.phase !== 'working' && turn.credits !== undefined ? formatCredits(turn.credits) : undefined;
  return (
    <>
      {/* The conversation is the experiment's, shared by everyone who can open it. */}
      {turn.sender && <SenderRow user={turn.sender} name={senderName(turn.sender)} />}
      <UserMessage text={turn.question} />
      <AiMessage barsState={phase ?? 'idle'} footnote={footnote}>
        {turn.answer && <AnswerMarkdown text={turn.answer} />}
        {turn.stale && turn.answer && (
          <p data-results-ai-stale="" className="mt-2 text-micro text-dim">
            The result has changed since this answer.
          </p>
        )}
        {phase === 'thinking' && (
          <WorkingSteps
            steps={turn.steps.length > 0 ? turn.steps : ['Reading the results']}
            phase="thinking"
            live={turn.credits !== undefined ? formatCredits(turn.credits) : undefined}
          />
        )}
        {turn.phase === 'stopped' && <p className="mt-2 text-micro text-dim">{manager.stoppedMessage}</p>}
        {turn.phase === 'errored' && turn.error && <TurnError turn={turn} manager={manager} />}
      </AiMessage>
    </>
  );
};

const TurnError = ({ turn, manager }: TurnViewProps) => {
  const isLatest = manager.turns.at(-1)?.key === turn.key;
  return (
    <div className={turn.answer ? 'mt-2' : undefined}>
      <p role="alert">{turn.error?.message}</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {turn.error?.status === 402 && manager.billingUrl && (
          <NavLink to="/settings/billing" className="text-micro font-medium text-accent hover:underline">
            Buy a credit pack
          </NavLink>
        )}
        {turn.error?.retryable && isLatest && (
          <TextButton onClick={() => manager.retry(turn.key)}>
            <RotateCcw aria-hidden="true" className="size-3" />
            Try again
          </TextButton>
        )}
      </div>
    </div>
  );
};

/**
 * Says once that an answer is ready: the log itself is silent (aria-live off), or every streamed
 * word would be read out as it arrives.
 */
type AnswerAnnouncerProps = { turn: ResultsTurn | undefined };

const AnswerAnnouncer = ({ turn }: AnswerAnnouncerProps) => {
  const message =
    turn?.phase === 'done' ? 'Answer ready' : turn?.phase === 'errored' ? 'The answer could not be finished' : '';
  return (
    <p role="status" aria-live="polite" className="sr-only">
      {message}
    </p>
  );
};

const ResultsInput = ({ manager }: ManagerProps) => {
  const { acknowledged } = manager;

  // Focus management on open, not data fetching. Here rather than in the panel: this mounts inside
  // the side slot's portal, so the box exists when it runs. Before the notice is accepted the box
  // is disabled, and its OK takes focus instead.
  useEffect(() => {
    const input = document.getElementById(RESULTS_AI_INPUT_ID) as HTMLTextAreaElement | null;
    const target =
      input && !input.disabled ? input : document.querySelector<HTMLElement>('[data-results-ai-panel] [data-ai-focus]');
    target?.focus({ preventScroll: true });
  }, []);

  return (
    <ChatInputBox
      inputId={RESULTS_AI_INPUT_ID}
      label="Ask about these results"
      value={manager.draft}
      onChange={manager.setDraft}
      onKeyDown={manager.onInputKeyDown}
      placeholder={acknowledged ? 'Ask about this experiment…' : 'Press OK on the notice above to start'}
      disabled={!acknowledged}
      busy={manager.busy}
      phase={manager.phase}
      canSend={manager.canSend}
      onSend={manager.submit}
      onStop={manager.stop}
      status={manager.statusLine}
      statusAlert={manager.outOfCredits ? { id: RESULTS_AI_OUT_OF_CREDITS_ID, text: 'Out of credits' } : undefined}
      meter={manager.meter}
    />
  );
};
